import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { SimulationCard } from '@/components/simulations/SimulationCard';
import { SimulationListView } from '@/components/simulations/SimulationListView';
import type { Simulation } from '@/data/mockAgents';
import { fetchUserSimulations } from '@/lib/api';
import {
  Search,
  Grid3x3,
  List,
  Activity,
  PlayCircle,
  CheckCircle,
  Target,
  Loader2,
  Theater,
  GraduationCap,
  ClipboardCheck,
} from 'lucide-react';

type AgentCategory = 'all' | 'roleplays' | 'coaches' | 'examiners';
type SubFilter = 'all' | string;

const CATEGORY_CONFIG: Record<AgentCategory, { label: string; icon: React.ReactNode; types: string[]; subFilters: { value: string; label: string }[] }> = {
  all: { label: 'All Types', icon: <Activity size={16} />, types: [], subFilters: [] },
  roleplays: {
    label: 'Roleplays',
    icon: <Theater size={16} />,
    types: ['patient', 'customer'],
    subFilters: [
      { value: 'all', label: 'All Roleplays' },
      { value: 'patient', label: 'Patient' },
      { value: 'customer', label: 'Roleplay' },
    ],
  },
  coaches: {
    label: 'Coaches',
    icon: <GraduationCap size={16} />,
    types: ['coach'],
    subFilters: [
      { value: 'all', label: 'All Coaches' },
      { value: 'consultative', label: 'Consultative' },
      { value: 'challenger', label: 'Challenging' },
      { value: 'socratic', label: 'Socratic' },
    ],
  },
  examiners: {
    label: 'Examiners',
    icon: <ClipboardCheck size={16} />,
    types: ['examiner'],
    subFilters: [
      { value: 'all', label: 'All Examiners' },
      { value: 'osce_standard', label: 'OSCE Standard' },
      { value: 'strict', label: 'Strict' },
      { value: 'supportive', label: 'Supportive' },
      { value: 'prompt_limited', label: 'Prompt-Limited' },
      { value: 'safety_first', label: 'Safety-First' },
      { value: 'communication_skills', label: 'Communication' },
      { value: 'clinical_reasoning', label: 'Clinical Reasoning' },
    ],
  },
};

export function MySimulations() {
  const { user } = useAuth();
  const { tenant } = useTenant();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<
    'all' | 'not-started' | 'in-progress' | 'completed'
  >('all');
  const [agentCategory, setAgentCategory] = useState<AgentCategory>('all');
  const [subFilter, setSubFilter] = useState<SubFilter>('all');
  const [contentCategory, setContentCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [loading, setLoading] = useState(true);
  const [simulations, setSimulations] = useState<Simulation[]>([]);

  useEffect(() => {
    async function loadSimulations() {
      if (!user) return;
      setLoading(true);
      try {
        // Pass current tenant_id to filter simulations to this tenant only
        const data = await fetchUserSimulations(user.id, tenant.tenant_id);
        setSimulations(data);
      } catch (error) {
        console.error('Failed to load simulations:', error);
      } finally {
        setLoading(false);
      }
    }
    loadSimulations();
  }, [user, tenant.tenant_id]);

  // Distinct content categories (agent_category) present in the user's simulations
  const categoryOptions = useMemo(() => {
    const labels = new Set<string>();
    simulations.forEach((sim) => {
      const label = (sim.category || '').trim();
      if (label) labels.add(label);
    });
    return Array.from(labels).sort((a, b) => a.localeCompare(b));
  }, [simulations]);

  // Filtering logic
  const filteredSimulations = useMemo(() => {
    let filtered = simulations;

    // Content category filter (agent_category)
    if (contentCategory !== 'all') {
      filtered = filtered.filter((sim) => (sim.category || '').trim() === contentCategory);
    }

    // Agent category filter
    if (agentCategory !== 'all') {
      const config = CATEGORY_CONFIG[agentCategory];
      filtered = filtered.filter((sim) => config.types.includes(sim.type));

      // Sub-filter within category
      if (subFilter !== 'all') {
        if (agentCategory === 'roleplays') {
          // Sub-filter by specific type (patient or customer)
          filtered = filtered.filter((sim) => sim.type === subFilter);
        } else if (agentCategory === 'coaches') {
          // Sub-filter by coaching style
          filtered = filtered.filter((sim) => sim.coaching_style === subFilter);
        } else if (agentCategory === 'examiners') {
          // Sub-filter by examiner preset
          filtered = filtered.filter((sim) => sim.examiner_personality === subFilter);
        }
      }
    }

    // Status filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter((sim) => sim.status === activeFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (sim) =>
          sim.name.toLowerCase().includes(query) ||
          sim.specialty.toLowerCase().includes(query) ||
          sim.title.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [simulations, searchQuery, activeFilter, agentCategory, subFilter, contentCategory]);

  // Summary stats calculations
  const inProgressCount = simulations.filter(
    (s) => s.status === 'in-progress'
  ).length;
  const completedCount = simulations.filter(
    (s) => s.status === 'completed'
  ).length;
  const goalsAchievedCount = simulations.filter(
    (s) => s.userBestScore && s.userBestScore >= s.goalScore
  ).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading your simulations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1800px] animate-fade-in p-4 sm:p-6 lg:p-8">
      {/* Page Header */}
      <div className="mb-5">
        <h1 className="mb-1 text-3xl font-bold text-card-foreground">My Simulations</h1>
        <p className="text-base text-muted-foreground">
          Access all your simulation training modules and track your progress
        </p>
      </div>

      {/* Agent Category Filter */}
      <div className="sticky top-0 z-20 mb-4 rounded-xl border border-border bg-card/95 p-3 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/90">
        <div className="flex flex-col gap-2.5">
          {/* Primary Category Pills */}
          <div className="flex gap-2 overflow-x-auto pb-0.5 sm:flex-wrap sm:overflow-visible">
            {(Object.entries(CATEGORY_CONFIG) as [AgentCategory, typeof CATEGORY_CONFIG[AgentCategory]][]).map(([key, config]) => (
              <button
                key={key}
                aria-pressed={agentCategory === key}
                onClick={() => {
                  setAgentCategory(key);
                  setSubFilter('all');
                }}
                className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${
                  agentCategory === key
                    ? 'bg-primary text-primary-foreground shadow-md'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                }`}
              >
                {config.icon}
                {config.label}
                {key !== 'all' && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                    agentCategory === key
                      ? 'bg-primary-foreground/20 text-primary-foreground'
                      : 'bg-background text-muted-foreground'
                  }`}>
                    {simulations.filter(s => config.types.includes(s.type)).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Sub-filter Pills (shown when a category with sub-filters is selected) */}
          {agentCategory !== 'all' && CATEGORY_CONFIG[agentCategory].subFilters.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto border-l-2 border-primary/30 pl-2 sm:flex-wrap sm:overflow-visible">
              {CATEGORY_CONFIG[agentCategory].subFilters.map((sf) => (
                <button
                  key={sf.value}
                  aria-pressed={subFilter === sf.value}
                  onClick={() => setSubFilter(sf.value)}
                  className={`min-h-11 shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    subFilter === sf.value
                      ? 'bg-primary/15 text-primary border border-primary/30'
                      : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground border border-transparent'
                  }`}
                >
                  {sf.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filter Bar & Search */}
      <div className="mb-5 rounded-xl border border-border bg-card p-3 shadow-sm">
        <div className="grid gap-3 xl:grid-cols-[minmax(240px,1fr)_minmax(190px,280px)_auto_auto] xl:items-center">
          {/* Search Bar */}
          <div>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                size={20}
              />
              <input
                type="text"
                aria-label="Search simulations"
                placeholder="Search simulations by name, specialty, or condition..."
                className="min-h-11 w-full rounded-lg border border-input bg-background py-2.5 pl-10 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-transparent focus:ring-2 focus:ring-ring"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {/* Category Filter (agent_category) */}
          <select
            value={contentCategory}
            onChange={(e) => setContentCategory(e.target.value)}
            aria-label="Filter by category"
            className="min-h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm font-medium text-foreground focus:border-transparent focus:ring-2 focus:ring-ring"
          >
            <option value="all">All Categories</option>
            {categoryOptions.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>

          {/* Status Filter Buttons */}
          <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 xl:flex">
            <button
              aria-pressed={activeFilter === 'all'}
              onClick={() => setActiveFilter('all')}
              className={`min-h-11 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeFilter === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All Status
            </button>
            <button
              aria-pressed={activeFilter === 'not-started'}
              onClick={() => setActiveFilter('not-started')}
              className={`min-h-11 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeFilter === 'not-started'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Not Started
            </button>
            <button
              aria-pressed={activeFilter === 'in-progress'}
              onClick={() => setActiveFilter('in-progress')}
              className={`min-h-11 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeFilter === 'in-progress'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              In Progress
            </button>
            <button
              aria-pressed={activeFilter === 'completed'}
              onClick={() => setActiveFilter('completed')}
              className={`min-h-11 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeFilter === 'completed'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Completed
            </button>
          </div>

          {/* View Toggle (Grid/List) */}
          <div className="flex w-fit rounded-lg bg-muted p-1 xl:justify-self-end">
            <button
              onClick={() => setViewMode('grid')}
              className={`min-h-10 min-w-10 rounded p-2 transition-colors ${
                viewMode === 'grid'
                  ? 'bg-card shadow text-card-foreground'
                  : 'text-muted-foreground hover:text-card-foreground'
              }`}
              aria-label="Grid view"
              aria-pressed={viewMode === 'grid'}
            >
              <Grid3x3 size={20} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`min-h-10 min-w-10 rounded p-2 transition-colors ${
                viewMode === 'list'
                  ? 'bg-card shadow text-card-foreground'
                  : 'text-muted-foreground hover:text-card-foreground'
              }`}
              aria-label="List view"
              aria-pressed={viewMode === 'list'}
            >
              <List size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats Bar */}
      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="rounded-xl border border-border bg-gradient-to-br from-primary-subtle to-card p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary">
              <Activity className="text-primary-foreground" size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold text-card-foreground">
                {filteredSimulations.length}
              </div>
              <div className="text-sm text-muted-foreground">Available</div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-gradient-to-br from-blue-50 to-card p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-600">
              <PlayCircle className="text-white" size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold text-card-foreground">
                {inProgressCount}
              </div>
              <div className="text-sm text-muted-foreground">In Progress</div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-gradient-to-br from-green-50 to-card p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-600">
              <CheckCircle className="text-white" size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold text-card-foreground">
                {completedCount}
              </div>
              <div className="text-sm text-muted-foreground">Completed</div>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-gradient-to-br from-orange-50 to-card p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-600">
              <Target className="text-white" size={20} />
            </div>
            <div>
              <div className="text-2xl font-bold text-card-foreground">
                {goalsAchievedCount}
              </div>
              <div className="text-sm text-muted-foreground">Goals Met</div>
            </div>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredSimulations.length === 0 && (
        <div className="text-center py-16">
          <Activity size={64} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold text-card-foreground mb-2">
            No simulations found
          </h3>
          <p className="text-muted-foreground mb-4">
            Try adjusting your search or filters
          </p>
          <button
            onClick={() => {
              setSearchQuery('');
              setActiveFilter('all');
              setAgentCategory('all');
              setSubFilter('all');
              setContentCategory('all');
            }}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary-light transition-colors"
          >
            Clear filters
          </button>
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && filteredSimulations.length > 0 && (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {filteredSimulations.map((sim) => (
            <SimulationCard key={sim.id} simulation={sim} />
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && filteredSimulations.length > 0 && (
        <SimulationListView simulations={filteredSimulations} />
      )}
    </div>
  );
}
